import { Card, Col, Row, Typography, theme as antdTheme } from 'antd';
import {
  CalendarOutlined,
  GiftOutlined,
  RightOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { PageHeader } from '@/shared/components/page-header';
import { useAbility } from '@/auth/ability-provider';
import type { Actions, Subjects } from '@/auth/ability';

interface Tile {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  /** Ability gate. Tile is hidden when `ability.can(action, subject)` is false. Omit to always show. */
  can?: { action: Actions; subject: Subjects };
}

const tiles: Tile[] = [
  {
    to: '/attendance/daily',
    title: 'Daily marking',
    description: 'Mark a class·section for a specific day. Reuses the prior submission if you re-open the sheet.',
    icon: <ScheduleOutlined />,
    can: { action: 'create', subject: 'Attendance' },
  },
  {
    to: '/attendance/monthly',
    title: 'Monthly summary',
    description: 'Per-student totals + per-day status across a calendar month.',
    icon: <CalendarOutlined />,
  },
  {
    to: '/attendance/holidays',
    title: 'Holidays & schedule',
    description: 'Manage school holidays and the date-effective weekly-off schedule.',
    icon: <GiftOutlined />,
    can: { action: 'manage', subject: 'Holiday' },
  },
];

export function AttendanceHubPage() {
  const { token } = antdTheme.useToken();
  const ability = useAbility();
  const visibleTiles = tiles.filter((t) => !t.can || ability.can(t.can.action, t.can.subject));
  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <PageHeader
        eyebrow="Module"
        title="Attendance"
        description="Daily student marking, monthly summary, holiday and weekly-off schedule. Staff attendance lands with the HR module."
      />

      <Row gutter={[16, 16]}>
        {visibleTiles.map((t) => (
          <Col key={t.to} xs={24} md={8}>
            <Link to={t.to} style={{ textDecoration: 'none' }}>
              <Card
                hoverable
                className="lift-card"
                style={{ height: '100%', borderColor: token.colorBorderSecondary }}
                styles={{ body: { padding: 20 } }}
              >
                <div style={{ display: 'flex', gap: 14 }}>
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      background: token.colorFillTertiary,
                      color: token.colorPrimary,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {t.icon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <Typography.Text strong style={{ fontSize: 15 }}>
                        {t.title}
                      </Typography.Text>
                      <RightOutlined style={{ fontSize: 12, color: token.colorTextTertiary }} />
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                      {t.description}
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>
    </div>
  );
}
